export interface TrendPoint {
  label: string;
  value: number | null;
}

export default function TrendChart({
  points,
  band,
  target,
  unit,
  height = 160,
}: {
  points: TrendPoint[];
  /** Optional shaded reference band, e.g. weight-gain range or tolerance band. */
  band?: { low: number; high: number };
  target?: number;
  unit: string;
  height?: number;
}) {
  const width = 320;
  const padding = 24;
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  const allValues = [...values, band?.low, band?.high, target].filter(
    (v): v is number => v !== undefined
  );
  const min = Math.min(...allValues, ...values);
  const max = Math.max(...allValues, ...values);
  const range = max - min || 1;

  const x = (i: number) => padding + (i / Math.max(1, points.length - 1)) * (width - padding * 2);
  const y = (v: number) => height - padding - ((v - min) / range) * (height - padding * 2);

  const linePath = points
    .map((p, i) => (p.value === null ? null : `${i === 0 || points[i - 1].value === null ? "M" : "L"} ${x(i)} ${y(p.value)}`))
    .filter(Boolean)
    .join(" ");

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} role="img">
      {band && (
        <rect
          x={padding}
          y={y(band.high)}
          width={width - padding * 2}
          height={Math.max(0, y(band.low) - y(band.high))}
          fill="#4A6F5E"
          opacity={0.12}
        />
      )}
      {target !== undefined && (
        <line
          x1={padding}
          x2={width - padding}
          y1={y(target)}
          y2={y(target)}
          stroke="#8FAE99"
          strokeDasharray="4 4"
        />
      )}
      {linePath && <path d={linePath} fill="none" stroke="#2F4B3C" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />}
      {points.map((p, i) =>
        p.value === null ? null : (
          <circle key={i} cx={x(i)} cy={y(p.value)} r={3} fill="#2F4B3C" />
        )
      )}
      <text x={padding} y={14} className="fill-forest-500" fontSize={10}>
        {unit}
      </text>
    </svg>
  );
}
